import { ApportionmentItem, isQuantumItem } from "./apportionmentTypes.ts";
import {
  Case,
  Claim,
  Damage,
  Issue,
  Quantum,
  SettlementOffer,
} from "./CaseModel.ts";
import randomId from "./randomId.ts";
import { WeightedValue } from "./WeightedValue.ts";

/**
 * A claimed amount as stored before damages were records of their own: it
 * carried an id of its own and the damage's name, and claims shared a damage
 * by sharing the name.
 */
export type LegacyQuantum = {
  id: string;
  name: string;
  value: number;
  awardedShares: WeightedValue[];
  liabilityShares?: { [defendantId: string]: number };
};

type Stored = Quantum | LegacyQuantum;

const isLegacy = (quantum: Stored): quantum is LegacyQuantum =>
  !("damageId" in quantum) && "id" in quantum && "name" in quantum;

/** Every amount of a claim, its own and its issues', with where it sits. */
function amountsOf(claim: Claim): Stored[][] {
  const lists: Stored[][] = [];
  const visit = (issue: Issue) => {
    if (issue.quantums) lists.push(issue.quantums as Stored[]);
    issue.subIssues?.forEach(visit);
  };
  visit(claim);
  claim.issues?.forEach(visit);
  return lists;
}

/** True while any amount of the case is still in the shape before damages. */
export function needsDamagesConversion(c: Pick<Case, "claims">): boolean {
  return (c.claims ?? []).some((claim) =>
    amountsOf(claim).some((amounts) => amounts.some(isLegacy)),
  );
}

const nameKey = (claimantId: string, name: string) =>
  `${claimantId}\u0000${name.trim()}`;

/**
 * Turns the amounts of a case stored before damages were records of their own
 * into damages: a claimant's amounts of one name (trimmed) become one damage,
 * whose id is the id of the first of them; the offers' references to any of
 * them name that damage. A case already converted comes back as it is.
 */
export function convertToDamages(
  c: Pick<Case, "claims" | "damages" | "settlements">,
): {
  claims: Claim[];
  damages: Damage[];
  settlements: SettlementOffer[];
} {
  const claims: Claim[] = structuredClone(c.claims ?? []);
  const damages: Damage[] = structuredClone(c.damages ?? []);

  // the damages known by claimant and name: those there are, then new ones
  const byName = new Map<string, Damage>(
    damages.map((damage) => [nameKey(damage.claimantId, damage.name), damage]),
  );
  // old amount id → damage id
  const renamed = new Map<string, string>();

  for (const claim of claims) {
    const claimantId = claim.claimant.id;
    for (const amounts of amountsOf(claim)) {
      for (let index = 0; index < amounts.length; index++) {
        const amount = amounts[index];
        if (!isLegacy(amount)) continue;
        const name = amount.name.trim();
        const key = nameKey(claimantId, name);
        let damage = byName.get(key);
        if (!damage) {
          damage = { id: amount.id, claimantId, name };
          byName.set(key, damage);
          damages.push(damage);
        }
        renamed.set(amount.id, damage.id);
        const { value, awardedShares, liabilityShares } = amount;
        amounts[index] = {
          damageId: damage.id,
          value,
          awardedShares,
          ...(liabilityShares ? { liabilityShares } : {}),
        };
      }
    }
    // a claim claims a damage once: of two amounts of it, the higher stays
    for (const amounts of amountsOf(claim)) {
      const kept = new Map<string, Quantum>();
      for (const amount of amounts as Quantum[]) {
        const other = kept.get(amount.damageId);
        if (!other || amount.value > other.value)
          kept.set(amount.damageId, amount);
      }
      amounts.splice(0, amounts.length, ...kept.values());
    }
  }

  // An offer stored without either reference keeps it absent, as every reader
  // of offers allows for; an entry of `settledQuantums` stored as `{ id }` is
  // read by its id, as the settlements page reads it.
  const settlements = (c.settlements ?? []).map((offer) => {
    const storedSettled: (string | { id: string })[] | undefined =
      offer.settledQuantums;
    const storedItems: ApportionmentItem[] | undefined = offer.apportionment;
    const settledQuantums = storedSettled && [
      ...new Set(
        storedSettled.map((entry) => {
          const id = typeof entry === "string" ? entry : entry.id;
          return renamed.get(id) ?? id;
        }),
      ),
    ];
    const apportionment =
      storedItems &&
      mergeItems(
        storedItems.map((item) =>
          isQuantumItem(item)
            ? {
                ...item,
                quantumId: renamed.get(item.quantumId) ?? item.quantumId,
              }
            : item,
        ),
      );
    const changed =
      JSON.stringify(settledQuantums) !== JSON.stringify(storedSettled) ||
      JSON.stringify(apportionment) !== JSON.stringify(storedItems);
    if (!changed) return offer;
    return {
      ...offer,
      ...(settledQuantums && { settledQuantums }),
      ...(apportionment && { apportionment }),
    };
  });

  return { claims, damages, settlements };
}

/**
 * The items of one damage that an offer allocated to separately, one item: on
 * if either was, its amount the sum of theirs where both had one.
 */
function mergeItems(items: ApportionmentItem[]): ApportionmentItem[] {
  const merged: ApportionmentItem[] = [];
  for (const item of items) {
    const same = isQuantumItem(item)
      ? merged.find(
          (other) => isQuantumItem(other) && other.quantumId === item.quantumId,
        )
      : undefined;
    if (!same) {
      merged.push(item);
      continue;
    }
    same.enabled = same.enabled || item.enabled;
    same.specificAmount =
      same.specificAmount !== undefined && item.specificAmount !== undefined
        ? same.specificAmount + item.specificAmount
        : undefined;
  }
  return merged;
}

/**
 * Gives every amount a damage of its claim's claimant. An amount whose damage
 * is another claimant's, as after its claim changed claimant, claims the
 * claimant's damage of that name, or a new damage of that name. Undefined
 * where every amount has one already.
 */
export function rehomeDamages(
  c: Pick<Case, "claims" | "damages">,
): { claims: Claim[]; damages: Damage[] } | undefined {
  const damages = [...(c.damages ?? [])];
  let changed = false;
  const claims = (c.claims ?? []).map((claim) => {
    const claimantId = claim.claimant.id;
    const copy = structuredClone(claim);
    for (const amounts of amountsOf(copy) as Quantum[][])
      for (const amount of amounts) {
        const damage = damages.find(({ id }) => id === amount.damageId);
        if (damage === undefined || damage.claimantId === claimantId) continue;
        let own = damages.find(
          (other) =>
            other.claimantId === claimantId && other.name === damage.name,
        );
        if (own === undefined) {
          own = { id: randomId(), claimantId, name: damage.name };
          damages.push(own);
        }
        amount.damageId = own.id;
        changed = true;
      }
    return copy;
  });
  return changed ? { claims, damages } : undefined;
}

/**
 * A case as the current code reads it: amounts stored before damages were
 * records of their own are converted in memory, as every save converts them
 * (see convertToDamages), so a case read before it was converted is read with
 * the damages it is saved with.
 */
export function withDamages<
  C extends Pick<Case, "claims" | "damages" | "settlements">,
>(c: C): C {
  if (!needsDamagesConversion(c)) return c;
  const { claims, damages, settlements } = convertToDamages(c);
  return { ...c, claims, damages, settlements };
}

/** The damages without those no claim of the case claims any more. */
export function claimedDamages(c: Pick<Case, "claims" | "damages">): Damage[] {
  const claimed = new Set(
    (c.claims ?? []).flatMap((claim) =>
      amountsOf(claim).flatMap((amounts) =>
        (amounts as Quantum[]).map(({ damageId }) => damageId),
      ),
    ),
  );
  return (c.damages ?? []).filter(({ id }) => claimed.has(id));
}

/** The name of a damage, by its id. */
export const damageName = (
  damages: Damage[] | undefined,
  damageId: string,
): string => damages?.find(({ id }) => id === damageId)?.name ?? damageId;
